import { ActionTree, GetterTree, MutationTree } from "vuex";
import { get } from "../services/api";
import { rootState } from "../types/store/state";

export interface Availability {
  name: string;
  data: string[];
}

export interface AvailabilityModule {
  namespaced: boolean;
  state: () => AvailabilityState;
  getters: GetterTree<AvailabilityState, rootState>;
  actions: ActionTree<AvailabilityState, rootState>;
  mutations: MutationTree<AvailabilityState>;
}

export interface AvailabilityState {
  availability: Availability[];
  outages: OutAges[];
  monthlyAvailability: {
    thisMonth: {
      raw: number | null;
      adj: number | null;
    };
    lastMonth: {
      raw: number | null;
      adj: number | null;
    };
  };
}

export interface OutAges {
  outages: string;
}

const availability_module: AvailabilityModule = {
  namespaced: true,
  state: () => ({
    availability: [],
    outages: [],
    monthlyAvailability: {
      thisMonth: {
        raw: null,
        adj: null,
      },
      lastMonth: {
        raw: null,
        adj: null,
      },
    },
  }),
  getters: {
    availability: (state) => {
      return state.availability;
    },
    monthlyAvailability: (state) => {
      return state.monthlyAvailability;
    },
    outages: (state) => {
      return state.outages;
    },
  },
  actions: {
    getAvailability({ commit }, data) {
      return new Promise((resolve, reject) => {
        const { service_id, range_type, start_date, end_date } = data;
        get(
          `tdapi/service/${service_id}/statusData?range_type=${range_type}&start_date=${start_date}&end_date=${end_date}`
        )
          .then((res) => {
            commit("updateAvailabilities", res.data);
            resolve(null);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
    getMonthlyAvailability({ commit }, data) {
      return new Promise((resolve, reject) => {
        const { service_id, range_type, start_date, end_date } = data;
        get(
          `tdapi/service/${service_id}/statusData?range_type=${range_type}&start_date=${start_date}&end_date=${end_date}`
        )
          .then((res) => {
            commit("updateMonthlyAvailabilities", res.data);
            resolve(null);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
  },
  mutations: {
    updateAvailabilities: (state, data) => {
      if (data.availability) {
        state.availability = data.availability;
      }
      if (data.outages && data.outages.outages) {
        try {
          const formatedRows = data.outages.outages
            .filter((row: { case_number: string }) => {
              return row.case_number;
            })
            .map((element: { duration: string; type: string; reason: string }) => {
              if (Number(element.duration) > 60) {
                element.duration = Math.floor(Number(element.duration) / 60) + " hours";
              } else {
                element.duration = Math.floor(Number(element.duration)) + " minutes";
              }
              element.type = element.type[0].toUpperCase() + element.type.substring(1, element.type.length);
              element.reason = element.reason
                .split("_")
                .map((element) => {
                  return element[0].toUpperCase() + element.substring(1, element.length);
                })
                .join(" ");
              return element;
            });
          state.outages = formatedRows;
        } catch (error) {
          console.log(error);
        }
      }
    },

    updateMonthlyAvailabilities: (state, data) => {
      if (!data?.availability) {
        return;
      }

      const now = new Date();
      const currentYear = now.getUTCFullYear();
      const currentMonth = now.getUTCMonth();
      const lastMonthDate = new Date(Date.UTC(currentYear, currentMonth - 1, 1));
      const lastYear = lastMonthDate.getUTCFullYear();
      const lastMonth = lastMonthDate.getUTCMonth();

      const latest: Record<"thisRaw" | "lastRaw" | "thisAdj" | "lastAdj", { ts: number; val: number } | null> = {
        thisRaw: null,
        lastRaw: null,
        thisAdj: null,
        lastAdj: null,
      };

      data.availability.forEach((avl: { data: any[]; name: string }) => {
        const isRaw = avl.name.includes("RAVL");

        avl.data.forEach(([iso, v]) => {
          const ts = Date.parse(iso + "T00:00:00Z");
          const d = new Date(ts);
          const y = d.getUTCFullYear();
          const m = d.getUTCMonth();

          const bucket =
            y === currentYear && m === currentMonth
              ? isRaw
                ? "thisRaw"
                : "thisAdj"
              : y === lastYear && m === lastMonth
                ? isRaw
                  ? "lastRaw"
                  : "lastAdj"
                : null;

          if (!bucket) return;

          if (!latest[bucket] || ts > latest[bucket]!.ts) {
            latest[bucket] = { ts, val: Number(v) };
          }
        });
      });

      const round3 = (x: number) => (x === 100 ? 100 : Math.round(x * 1000) / 1000);

      state.monthlyAvailability.thisMonth.raw = latest.thisRaw ? round3(latest.thisRaw.val) : null;
      state.monthlyAvailability.thisMonth.adj = latest.thisAdj ? round3(latest.thisAdj.val) : null;
      state.monthlyAvailability.lastMonth.raw = latest.lastRaw ? round3(latest.lastRaw.val) : null;
      state.monthlyAvailability.lastMonth.adj = latest.lastAdj ? round3(latest.lastAdj.val) : null;
    },
  },
};

export default availability_module;
